import { Dialog } from "@mui/material"
import { Plus, X } from "lucide-mui"
import React from "react"

import i18n from "@/i18n"

type HowItWorksModalProps = {
  open: boolean
  onClose: () => void
}

type TableUnitState = "available" | "selected" | "unavailable"

const UNIT_COLORS: Record<TableUnitState, { table: string; tableBorder: string; chair: string }> = {
  available: { table: "#69c580", tableBorder: "#449f53", chair: "#c2eacc" },
  selected: { table: "#2596fa", tableBorder: "#1482e4", chair: "#76bfff" },
  unavailable: { table: "#ffffff", tableBorder: "#bec3cc", chair: "#f1f2f3" },
}

const LEGEND_ITEMS: { state: TableUnitState; key: string }[] = [
  { state: "available", key: "how_it_works_legend_available" },
  { state: "selected", key: "how_it_works_legend_selected" },
  { state: "unavailable", key: "how_it_works_legend_unavailable" },
]

// The legend's "Unavailable" chip has its own grey fill (spec `#EBEBEB`); the mini-plan unavailable
// *tables* stay white/no-fill (correct per the map spec), so the swatch can't just reuse UNIT_COLORS.
const LEGEND_SWATCH_FILL: Partial<Record<TableUnitState, string>> = { unavailable: "#ebebeb" }

const SELECT_TABLE_STATES: TableUnitState[] = [
  "unavailable",
  "selected",
  "unavailable",
  "unavailable",
  "unavailable",
  "unavailable",
]
const SELECT_TIME_STATES: TableUnitState[] = [
  "available",
  "unavailable",
  "available",
  "available",
  "available",
  "unavailable",
]

// Illustrative example content (not translatable copy): the mini timeline window + hour axis.
const EXAMPLE_WINDOW = "18:00 - 21:00"
const EXAMPLE_HOURS = ["17:00", "18:00", "19:00", "20:00", "21:00", "22:00"]

const label = (key: string) => i18n.t(`app.public.venue.table_selection.${key}`)

// A single table unit (square + two chairs top, two chairs bottom) inside the mini floor-plan SVG.
const renderTableUnit = (state: TableUnitState, x: number, y: number, key: string): React.ReactNode => {
  const colors = UNIT_COLORS[state]
  return (
    <g key={key}>
      <rect x={x + 5} y={y} width={7} height={4} rx={1.5} fill={colors.chair} />
      <rect x={x + 16} y={y} width={7} height={4} rx={1.5} fill={colors.chair} />
      <rect
        x={x + 3}
        y={y + 6}
        width={22}
        height={20}
        rx={3}
        fill={colors.table}
        stroke={colors.tableBorder}
        strokeWidth={1}
      />
      <rect x={x + 5} y={y + 28} width={7} height={4} rx={1.5} fill={colors.chair} />
      <rect x={x + 16} y={y + 28} width={7} height={4} rx={1.5} fill={colors.chair} />
    </g>
  )
}

const renderMiniPlan = (states: TableUnitState[]): React.ReactNode => (
  <div className="flex-1 rounded-md bg-[#f6f7f8] px-3 py-2.5">
    <svg viewBox="0 0 176 90" className="h-auto w-full" role="presentation">
      <rect x={0} y={8} width={3} height={74} rx={1.5} fill="#a9d2f5" />
      {states.map((state, index) =>
        renderTableUnit(state, 16 + (index % 3) * 54, 12 + Math.floor(index / 3) * 44, `unit-${index}`),
      )}
    </svg>
  </div>
)

const renderExplainerRow = (boldKey: string, restKey: string, states: TableUnitState[]): React.ReactNode => (
  <div className="flex items-center gap-4">
    <p className="w-[40%] shrink-0 text-[10px] leading-normal">
      <span className="font-semibold text-[#374151]">{label(boldKey)}</span>
      <span className="text-[#787a89]">{label(restKey)}</span>
    </p>
    {renderMiniPlan(states)}
  </div>
)

const renderExampleTimeline = (): React.ReactNode => (
  <div className="rounded-lg border border-[#ececec] pb-3 pt-2.5">
    <p className="border-b border-[#f0f0f0] pb-2 text-center text-[12px] font-medium leading-tight text-black">
      {label("how_it_works_example_table")}
    </p>

    <div className="px-3 pt-3">
      <div className="relative h-[26px]">
        <div className="absolute left-0 top-0 h-full w-[15%] rounded-[4px] bg-[#e4e6ea]" />
        <div className="absolute right-0 top-0 h-full w-[13%] rounded-[4px] bg-[#e4e6ea]" />
        <div className="absolute left-[20%] top-0 flex h-full w-[60%] items-center justify-center rounded-[4px] bg-[#69c580]">
          <span className="text-[10px] font-medium leading-none text-white">{EXAMPLE_WINDOW}</span>
          <span className="absolute -right-2 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-[#449f53] text-white">
            <Plus className="h-3 w-3" />
          </span>
        </div>
      </div>

      <div className="mt-2 flex justify-between text-[9px] leading-none text-[#9aa0aa]">
        {EXAMPLE_HOURS.map((hour) => (
          <span key={hour}>{hour}</span>
        ))}
      </div>
    </div>
  </div>
)

const renderLegend = (): React.ReactNode => (
  <div
    className="flex items-center border-t border-[#ececec] pt-4"
    data-testid="public-reservation-modal-mobile-howitworks-legend"
  >
    {LEGEND_ITEMS.map((item, index) => (
      <React.Fragment key={item.key}>
        {index > 0 ? <div className="h-4 w-px bg-[#ececec]" /> : null}
        <div className="flex flex-1 items-center justify-center gap-2">
          <span
            className="h-3 w-3 shrink-0 rounded-[3px] border"
            style={{
              backgroundColor: LEGEND_SWATCH_FILL[item.state] ?? UNIT_COLORS[item.state].table,
              borderColor: UNIT_COLORS[item.state].tableBorder,
            }}
          />
          <span className="text-[10px] leading-none text-[#5f6774]">{label(item.key)}</span>
        </div>
      </React.Fragment>
    ))}
  </div>
)

/**
 * "How it works?" bottom-sheet (Figma 1283-757). Always available on demand via the floor-tabs `(i)`
 * icon — independent of the coach-mark's once-only behavior. Follows the same `Dialog` bottom-sheet
 * pattern as the other mobile sheets; returns null when closed so it never mounts the `Dialog`.
 */
const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ open, onClose }) => {
  if (!open) return null

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.6)" } } }}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
      PaperProps={{
        "data-testid": "public-reservation-modal-mobile-howitworks-sheet",
        sx: {
          width: "100%",
          margin: 0,
          borderRadius: "20px 20px 0 0",
          maxHeight: "92dvh",
          overflow: "hidden",
        },
      }}
    >
      <div className="flex max-h-[inherit] min-h-0 flex-col">
        <div className="relative flex h-[50px] shrink-0 items-center justify-center px-5">
          <h2 className="text-[14px] font-medium leading-none text-black">{label("how_it_works")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-5 flex h-8 w-8 items-center justify-center text-[#171717]"
            aria-label={i18n.t("app.public.venue.modal.close")}
            data-testid="public-reservation-modal-mobile-howitworks-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-6 pt-1">
          {renderExplainerRow("how_it_works_select_table_bold", "how_it_works_select_table_rest", SELECT_TABLE_STATES)}

          {renderExampleTimeline()}

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-[#ececec]" />
            <span className="text-[10px] font-medium leading-none text-[#aeb2b9]">{label("how_it_works_or")}</span>
            <div className="h-px flex-1 bg-[#ececec]" />
          </div>

          {renderExplainerRow("how_it_works_select_time_bold", "how_it_works_select_time_rest", SELECT_TIME_STATES)}

          {renderLegend()}
        </div>
      </div>
    </Dialog>
  )
}

export default HowItWorksModal
